// ============================================================
//  API SHIM — FastAPI backend nahradený priamymi Supabase volaniami.
//  Zachováva axios rozhranie: api.get(path) → Promise<{ data }>
//  takže stránky (Dashboard, Vault, Inbox…) ostávajú bez zmien.
//
//  Externé integrácie (AI, Fanvue, Telegram push, mass-message)
//  idú cez Supabase Edge Functions — nasadzujú sa v KROKU 3.
// ============================================================
import { sb } from "./supabase";
import { toast } from "sonner";

// Push do reálneho Fanvue/Telegramu — pri zlyhaní upozorni, ale lokálnu správu nechaj
async function pushOut(fanId, text, extra = {}) {
  const attempt = () => edge("dash-fanvue", { action: "push", fan_id: fanId, text, ...extra });
  try {
    const res = await attempt();
    if (res?.price_note) {
      toast.warning('Fotka odišla, ale BEZ ceny: ' + res.price_note, { duration: 12000 });
    }
  } catch (e) {
    const d = e?.response?.data?.detail || "push zlyhal";
    // 429 = Fanvue rate limit → počkaj a skús ešte raz
    if (/429|Too many requests/i.test(String(d))) {
      toast.loading("Fanvue je zahltený — skúšam znova o 3 s…", { id: "rl", duration: 3000 });
      await new Promise((r) => setTimeout(r, 3000));
      try {
        await attempt();
        toast.success("Odoslané ✓", { id: "rl" });
        return;
      } catch (e2) {
        const d2 = e2?.response?.data?.detail || d;
        toast.error(`Fanvue odmieta správy (rate limit). Počkaj minútu a pošli znova.`, { id: "rl", duration: 8000 });
        return;
      }
    }
    toast.warning(`Správa uložená v dashi, ale NEODIŠLA na platformu: ${d}`, { duration: 8000 });
  }
}

// ── Pomocné ──────────────────────────────────────────────────
const ok = (data) => ({ data });

function apiErr(status, detail) {
  const e = new Error(detail);
  e.response = { status, data: { detail } };
  return e;
}

async function uid() {
  const { data } = await sb.auth.getSession();
  const id = data?.session?.user?.id;
  if (!id) {
    if (!window.location.pathname.startsWith("/login")) {
      window.location.href = "/login";
    }
    throw apiErr(401, "Nie si prihlásený");
  }
  return id;
}

function throwSb(error, status = 400) {
  if (error) throw apiErr(status, error.message || String(error));
}

const nowIso = () => new Date().toISOString();

// Edge Function volanie (KROK 3). Kým nie sú nasadené, vráti 503.
async function edge(fn, body) {
  const { data, error } = await sb.functions.invoke(fn, { body });
  if (error) {
    let detail = error.message || "Edge Function zlyhala";
    try {
      const ctx = await error.context?.json?.();
      if (ctx?.detail) detail = ctx.detail;
      else if (ctx?.error) detail = ctx.error;
    } catch (_) { /* ignore */ }
    // Len skutočné "funkcia neexistuje" (Supabase NOT_FOUND), nie 404 z externých API
    if (/^(function not found|requested function was not found)/i.test(detail) ||
        /NOT_FOUND/.test(detail) && /function/i.test(detail)) {
      detail = `Edge Function „${fn}" nie je nasadená`;
    }
    throw apiErr(503, detail);
  }
  return data;
}

// dataURI/base64 → Blob (upload do Storage)
function b64ToBlob(b64) {
  let mime = "image/jpeg";
  let raw = b64;
  const m = /^data:([^;]+);base64,(.*)$/s.exec(b64);
  if (m) { mime = m[1]; raw = m[2]; }
  const bin = atob(raw);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return { blob: new Blob([arr], { type: mime }), mime };
}

const extFromMime = (mime) =>
  ({ "image/png": "png", "image/webp": "webp", "image/gif": "gif" }[mime] || "jpg");

// ── FANS ─────────────────────────────────────────────────────
async function getOrCreateFan(userId, platform, externalId, name) {
  const { data: existing, error: e1 } = await sb
    .from("dash_fans")
    .select("*")
    .eq("user_id", userId)
    .eq("platform", platform)
    .eq("external_id", externalId)
    .maybeSingle();
  throwSb(e1);
  if (existing) return existing;

  const { data: created, error: e2 } = await sb
    .from("dash_fans")
    .insert({
      user_id: userId,
      platform,
      external_id: externalId,
      name: name || "Unknown",
    })
    .select()
    .single();
  throwSb(e2);
  return created;
}

async function listFans(userId) {
  const { data, error } = await sb
    .from("dash_fans")
    .select("*")
    .eq("user_id", userId)
    .order("last_message_at", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false });
  throwSb(error);
  return data || [];
}

// spend/nsfw logika po prijatej správe (kópia _update_fan_after_msg)
async function updateFanAfterMsg(userId, fan, amount, fromCreator) {
  const upd = { last_message_at: nowIso() };
  if (amount > 0 && !fromCreator) {
    upd.spend_total = Number(fan.spend_total || 0) + amount;
    upd.tips_total = Number(fan.tips_total || 0) + amount;
  }
  if (!fan.nsfw_unlocked) {
    const settings = await getAiSettings(userId);
    const threshold = Number(settings.nsfw_spend_threshold ?? 20);
    const newSpend =
      Number(fan.spend_total || 0) + (amount > 0 && !fromCreator ? amount : 0);
    if (newSpend >= threshold) upd.nsfw_unlocked = true;
  }
  const { error } = await sb.from("dash_fans").update(upd).eq("id", fan.id);
  throwSb(error);
}

// ── AI SETTINGS ──────────────────────────────────────────────
const AI_DEFAULTS = {
  custom_persona: null,
  auto_reply: true,
  nsfw_spend_threshold: 20,
  default_language: "auto",
  session_max_minutes: 60,
  bedtime_cooldown_hours: 8,
  bedtime_message: null,
  realistic_typing: true,
  typing_speed_cps: 5,
  min_send_delay_seconds: 5,
  max_send_delay_seconds: 120,
  llm_model: null,
};

async function getAiSettings(userId) {
  const { data, error } = await sb
    .from("dash_ai_settings")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();
  throwSb(error);
  return data || { ...AI_DEFAULTS, user_id: userId };
}

// ── PHOTOS ───────────────────────────────────────────────────
async function listPhotos(userId) {
  const { data, error } = await sb
    .from("dash_photos")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false, nullsFirst: false })
    .order("id", { ascending: false });
  throwSb(error);
  const rows = data || [];

  // Signed URL pre fotky v Storage buckete 'vault' — plná verzia + malý náhľad
  const paths = rows.filter((r) => r.storage_path).map((r) => r.storage_path);
  let signed = {}, thumbs = {};
  if (paths.length) {
    const { data: urls } = await sb.storage.from("vault").createSignedUrls(paths, 3600);
    (urls || []).forEach((u) => { if (u.signedUrl) signed[u.path] = u.signedUrl; });
    // Náhľady: rovnaké podpísané URL + transformácia na 300px (rýchle, needáva 5MB fotky do mriežky)
    const { data: turls } = await sb.storage.from("vault").createSignedUrls(paths, 3600, {
      transform: { width: 300, height: 400, resize: "cover", quality: 60 },
    });
    (turls || []).forEach((u) => { if (u.signedUrl) thumbs[u.path] = u.signedUrl; });
  }
  return rows.map((r) => {
    const full = r.storage_path ? signed[r.storage_path] || null : r.url || null;
    const thumb = r.storage_path ? (thumbs[r.storage_path] || full) : (r.url || null);
    return { ...r, image_url: full, thumbnail_url: thumb };
  });
}

async function createPhoto(userId, payload) {
  let storage_path = null;
  let url = payload.image_url || payload.url || null;

  if (payload.image_base64) {
    const { blob, mime } = b64ToBlob(payload.image_base64);
    storage_path = `${userId}/${crypto.randomUUID()}.${extFromMime(mime)}`;
    const { error: upErr } = await sb.storage
      .from("vault")
      .upload(storage_path, blob, { contentType: mime });
    throwSb(upErr);
    url = null;
  }
  if (!storage_path && !url) throw apiErr(400, "Chýba obrázok");

  const { data, error } = await sb
    .from("dash_photos")
    .insert({
      user_id: userId,
      title: payload.title || "",
      storage_path,
      url,
      price: Number(payload.price) || 0,
      tags: payload.tags || [],
      nsfw: !!payload.nsfw,
      source: payload.source || "upload",
      created_at: new Date().toISOString(),
    })
    .select()
    .single();
  throwSb(error);
  return data;
}

// ── DEMO SEED / CLEAR ────────────────────────────────────────
async function seedDemo(userId) {
  const demoFans = [
    { external_id: "demo_tomas",  name: "Tomáš K.",  platform: "fanvue",   spend_total: 145, tips_total: 45, nsfw_unlocked: true },
    { external_id: "demo_peter",  name: "Peter B.",  platform: "telegram", spend_total: 32,  tips_total: 12, nsfw_unlocked: true },
    { external_id: "demo_marcus", name: "Marcus W.", platform: "fanvue",   spend_total: 8,   tips_total: 0,  nsfw_unlocked: false },
  ];
  for (const f of demoFans) {
    const { data: fan, error } = await sb
      .from("dash_fans")
      .upsert(
        { user_id: userId, tags: ["demo"], last_message_at: nowIso(), ...f },
        { onConflict: "user_id,platform,external_id" }
      )
      .select()
      .single();
    throwSb(error);
    await sb.from("dash_messages").insert([
      { user_id: userId, fan_id: fan.id, platform: fan.platform, text: "hey, som tu nový 😏", from_creator: false },
      { user_id: userId, fan_id: fan.id, platform: fan.platform, text: "vitaj zlatko 💜 ako sa máš?", from_creator: true },
    ]);
  }
  return { ok: true, seeded: demoFans.length };
}

async function clearDemo(userId) {
  const { error } = await sb
    .from("dash_fans")
    .delete()
    .eq("user_id", userId)
    .contains("tags", ["demo"]);
  throwSb(error);
  return { ok: true };
}

// ── ANALYTICS ────────────────────────────────────────────────
async function analyticsOverview(userId) {
  let fanvueEarnings = null;
  try {
    const { data: conn } = await sb.from("dash_connections")
      .select("extra_earnings").eq("user_id", userId).eq("platform", "fanvue").maybeSingle();
    if (conn && conn.extra_earnings != null) fanvueEarnings = Number(conn.extra_earnings);
  } catch (_) { /* stĺpec ešte nemusí existovať */ }
  const [{ data: fans, error: e1 }, { count: msgsCount, error: e2 }, { data: recent, error: e3 }] =
    await Promise.all([
      sb.from("dash_fans").select("*").eq("user_id", userId),
      sb.from("dash_messages").select("id", { count: "exact", head: true }).eq("user_id", userId),
      sb.from("dash_messages").select("*").eq("user_id", userId)
        .order("created_at", { ascending: false }).limit(10),
    ]);
  throwSb(e1); throwSb(e2); throwSb(e3);

  const all = fans || [];
  const platforms = {};
  for (const f of all) {
    platforms[f.platform] = platforms[f.platform] || { _id: f.platform, count: 0, spend: 0 };
    platforms[f.platform].count += 1;
    platforms[f.platform].spend += Number(f.spend_total || 0);
  }
  return {
    total_revenue: all.reduce((s, f) => s + Number(f.spend_total || 0), 0),
    total_tips: all.reduce((s, f) => s + Number(f.tips_total || 0), 0),
    fans_count: all.length,
    messages_count: msgsCount || 0,
    nsfw_unlocked_count: all.filter((f) => f.nsfw_unlocked).length,
    top_fans: [...all].sort((a, b) => b.spend_total - a.spend_total).slice(0, 5),
    recent_activity: recent || [],
    platforms: Object.values(platforms),
  };
}

// ── ROUTER — mapovanie starých REST ciest na Supabase ───────
async function route(method, path, body, params) {
  const userId = await uid();
  const P = (re) => path.match(re);
  let m;

  // ---------- AUTH ----------
  if (method === "POST" && path === "/auth/change-password") {
    const { data: s } = await sb.auth.getSession();
    const email = s?.session?.user?.email;
    const { error: verr } = await sb.auth.signInWithPassword({
      email, password: body.current_password,
    });
    if (verr) throw apiErr(400, "Aktuálne heslo nesedí");
    const { error } = await sb.auth.updateUser({ password: body.new_password });
    throwSb(error);
    return ok({ ok: true });
  }

  // ---------- FANS ----------
  if (method === "GET" && path === "/fans") return ok(await listFans(userId));

  if ((m = P(/^\/fans\/([^/]+)$/))) {
    if (method === "GET") {
      const { data, error } = await sb.from("dash_fans").select("*")
        .eq("id", m[1]).eq("user_id", userId).maybeSingle();
      throwSb(error);
      if (!data) throw apiErr(404, "Fan not found");
      return ok(data);
    }
    if (method === "PATCH") {
      const { data, error } = await sb.from("dash_fans").update(body)
        .eq("id", m[1]).eq("user_id", userId).select().single();
      throwSb(error);
      return ok(data);
    }
  }
  if ((m = P(/^\/fans\/([^/]+)$/)) && method === "DELETE") {
    const { error } = await sb.from("dash_fans").delete()
      .eq("id", m[1]).eq("user_id", userId);
    throwSb(error);
    return ok({ ok: true });
  }
  if (method === "POST" && path === "/fans") {
    const fan = await getOrCreateFan(userId, body.platform, body.external_id, body.name);
    if (body.avatar_url) {
      await sb.from("dash_fans").update({ avatar_url: body.avatar_url }).eq("id", fan.id);
      fan.avatar_url = body.avatar_url;
    }
    return ok(fan);
  }

  // ---------- MESSAGES ----------
  if (method === "GET" && path === "/messages") {
    let q = sb.from("dash_messages").select("*").eq("user_id", userId);
    if (params?.fan_id) q = q.eq("fan_id", params.fan_id);
    if (params?.platform) q = q.eq("platform", params.platform);
    const { data, error } = await q.order("created_at", { ascending: false })
      .limit(params?.limit || 200);
    throwSb(error);
    return ok((data || []).reverse());
  }

  if (method === "POST" && path === "/messages/send") {
    const { data: fan, error: fe } = await sb.from("dash_fans").select("*")
      .eq("id", body.fan_id).eq("user_id", userId).maybeSingle();
    throwSb(fe);
    if (!fan) throw apiErr(404, "Fan not found");
    const { data: msg, error } = await sb.from("dash_messages").insert({
      user_id: userId, fan_id: fan.id, platform: fan.platform,
      text: body.text, from_creator: true,
    }).select().single();
    throwSb(error);
    await sb.from("dash_fans").update({ last_message_at: nowIso() }).eq("id", fan.id);
    await pushOut(fan.id, body.text);
    return ok(msg);
  }

  if (method === "POST" && path === "/messages/incoming") {
    const platform = ["fanvue", "telegram"].includes(body.platform) ? body.platform : "fanvue";
    const fan = await getOrCreateFan(userId, platform, body.fan_external_id, body.fan_name);
    const { data: msg, error } = await sb.from("dash_messages").insert({
      user_id: userId, fan_id: fan.id, platform,
      text: body.text, from_creator: !!body.from_creator,
      amount: Number(body.amount) || 0,
    }).select().single();
    throwSb(error);
    await updateFanAfterMsg(userId, fan, Number(body.amount) || 0, !!body.from_creator);
    // AI auto-reply — Edge Function (KROK 3), best effort
    let ai_response = null;
    // no_ai=true → len zapíš správu (simulácia v Schránke), AI nikdy neodpovie ani neodošle
    if (!body.no_ai && !body.from_creator && fan.ai_enabled !== false) {
      try {
        const settings = await getAiSettings(userId);
        if (settings.auto_reply) {
          ai_response = await edge("dash-ai", {
            action: "auto_reply", fan_id: fan.id, incoming: body.text,
          });
          // AI odpoveď pošli aj do reálneho Fanvue/Telegram chatu
          if (ai_response?.text) await pushOut(fan.id, ai_response.text);
        }
      } catch (_) { /* AI nenasadená alebo bez kreditov */ }
    }
    return ok({ message: msg, ai_response });
  }

  // ---------- PHOTOS / VAULT ----------
  if (method === "GET" && path === "/photos") return ok(await listPhotos(userId));
  if (method === "POST" && path === "/photos") return ok(await createPhoto(userId, body));
  if ((m = P(/^\/photos\/([^/]+)$/)) && method === "PATCH") {
    const allowed = ["ai_sendable", "ai_desc", "ai_level", "ai_free", "price", "category", "title", "tags", "nsfw"];
    const patch = {};
    for (const k of allowed) if (k in body) patch[k] = body[k];
    const { data, error } = await sb.from("dash_photos").update(patch)
      .eq("id", m[1]).eq("user_id", userId).select().single();
    throwSb(error);
    return ok(data);
  }
  if ((m = P(/^\/photos\/([^/]+)$/)) && method === "DELETE") {
    const { data: row } = await sb.from("dash_photos").select("storage_path")
      .eq("id", m[1]).eq("user_id", userId).maybeSingle();
    if (row?.storage_path) await sb.storage.from("vault").remove([row.storage_path]);
    const { error } = await sb.from("dash_photos").delete()
      .eq("id", m[1]).eq("user_id", userId);
    throwSb(error);
    return ok({ ok: true });
  }

  if (method === "POST" && path === "/photos/offer") {
    const photoIds = body.photo_ids?.length ? body.photo_ids : [body.photo_id].filter(Boolean);
    const { data: fan, error: fe } = await sb.from("dash_fans").select("*")
      .eq("id", body.fan_id).eq("user_id", userId).maybeSingle();
    throwSb(fe);
    if (!fan) throw apiErr(404, "Fan not found");
    const out = [];
    for (const pid of photoIds) {
      const { data: photo } = await sb.from("dash_photos").select("*")
        .eq("id", pid).eq("user_id", userId).maybeSingle();
      if (!photo) throw apiErr(404, "Photo not found");
      const price = body.custom_price ?? body.price ?? photo.price;
      const teaser = body.teaser ||
        `i made something just for you, baby 💜 wanna see? ($${Number(price).toFixed(0)}) 😏`;
      const { data: msg, error } = await sb.from("dash_messages").insert({
        user_id: userId, fan_id: fan.id, platform: fan.platform,
        text: teaser, from_creator: true, ppv_photo_id: pid, ppv_price: price,
      }).select().single();
      throwSb(error);
      // Reálne PPV na Fanvue: priložená fotka + cena (fotka musí byť nahraná ☁️)
      if (fan.platform === "fanvue") {
        if (!photo.fanvue_media_id) {
          toast.warning(`„${photo.title || "Fotka"}" nie je nahraná vo Fanvue (☁️) — odišiel len text bez fotky`);
          await pushOut(fan.id, teaser);
        } else {
          await pushOut(fan.id, teaser, {
            media_uuids: [photo.fanvue_media_id],
            price: Number(price) > 0 ? Number(price) : undefined,
          });
        }
      } else {
        await pushOut(fan.id, teaser);
      }
      // Pamäť + limity — ručné ponuky sa počítajú rovnako ako AI ponuky
      await sb.from("dash_photo_sends").upsert({
        user_id: userId, fan_id: fan.id, photo_id: pid,
        free: !(Number(price) > 0), price: Number(price) || 0,
      }, { onConflict: "fan_id,photo_id", ignoreDuplicates: true });
      const today = new Date().toISOString().slice(0, 10);
      const offersToday = fan.offers_today_date === today ? Number(fan.offers_today ?? 0) : 0;
      await sb.from("dash_fans").update({
        last_offer_at: nowIso(), offers_today: offersToday + 1, offers_today_date: today,
      }).eq("id", fan.id);
      await sb.from("dash_photos").update({
        times_offered: Number(photo.times_offered ?? 0) + 1,
      }).eq("id", pid);
      out.push(msg);
    }
    return ok(out.length === 1 ? out[0] : out);
  }

  // ---------- TIPS ----------
  if (method === "POST" && path === "/tips") {
    const { data: fan, error: fe } = await sb.from("dash_fans").select("*")
      .eq("id", body.fan_id).eq("user_id", userId).maybeSingle();
    throwSb(fe);
    if (!fan) throw apiErr(404, "Fan not found");
    const { data, error } = await sb.from("dash_tips").insert({
      user_id: userId, fan_id: fan.id,
      amount: Number(body.amount), note: body.note || null,
    }).select().single();
    throwSb(error);
    await updateFanAfterMsg(userId, fan, Number(body.amount), false);
    return ok(data);
  }

  // ---------- CONNECTIONS ----------
  if (method === "GET" && path === "/connections") {
    const { data, error } = await sb.from("dash_connections")
      .select("platform, username, connected_at").eq("user_id", userId);
    throwSb(error);
    return ok((data || []).map((c) => ({ ...c, connected: true })));
  }

  if (method === "POST" && path === "/connections/simple") {
    if (body.platform !== "telegram") throw apiErr(400, "Use OAuth flow for fanvue");
    let username = "@bot";
    try {
      const r = await fetch(`https://api.telegram.org/bot${body.token}/getMe`);
      const j = await r.json();
      if (!j.ok) throw new Error();
      username = "@" + (j.result?.username || "bot");
    } catch (_) {
      throw apiErr(400, "Neplatný Telegram bot token");
    }
    const { error } = await sb.from("dash_connections").upsert({
      user_id: userId, platform: "telegram",
      token: body.token, username, extra: body.extra || {}, connected_at: nowIso(),
    }, { onConflict: "user_id,platform" });
    throwSb(error);
    // Auto-registrácia webhooku — fanúšikovia sa pripoja cez t.me link
    let fan_link = null;
    try {
      const st = await edge("dash-telegram", { action: "setup" });
      fan_link = st?.fan_link || null;
      if (fan_link) toast.success(`Telegram pripojený — fanúšikom zdieľaj: ${fan_link}`, { duration: 9000 });
    } catch (e) {
      toast.warning("Bot uložený, ale webhook sa nezaregistroval: " +
        (e?.response?.data?.detail || "nasaď Edge Function dash-telegram"));
    }
    return ok({ ok: true, platform: "telegram", username, fan_link });
  }

  if ((m = P(/^\/connections\/([^/]+)$/)) && method === "DELETE") {
    if (m[1] === "telegram") {
      try { await edge("dash-telegram", { action: "teardown" }); } catch (_) { /* */ }
    }
    const { error } = await sb.from("dash_connections").delete()
      .eq("user_id", userId).eq("platform", m[1]);
    throwSb(error);
    return ok({ ok: true });
  }

  // ---------- AI SETTINGS ----------
  if (method === "GET" && path === "/ai/settings") return ok(await getAiSettings(userId));
  if (method === "PUT" && path === "/ai/settings") {
    const { data, error } = await sb.from("dash_ai_settings")
      .upsert({ user_id: userId, ...body, updated_at: nowIso() },
              { onConflict: "user_id" })
      .select().single();
    throwSb(error);
    return ok(data);
  }

  // ---------- ANALYTICS ----------
  if (method === "GET" && path === "/analytics/overview")
    return ok(await analyticsOverview(userId));

  // ---------- DEV ----------
  if (method === "POST" && path === "/dev/seed-demo") return ok(await seedDemo(userId));
  if (method === "POST" && path === "/dev/clear-demo") return ok(await clearDemo(userId));

  // ---------- EDGE FUNCTIONS (KROK 3) ----------
  if (method === "POST" && path === "/ai/draft")
    return ok(await edge("dash-ai", { action: "draft", ...body }));
  if (method === "POST" && path === "/ai/test")
    return ok(await edge("dash-ai", { action: "test", ...body }));
  if ((m = P(/^\/ai\/regenerate-notes\/([^/]+)$/)) && method === "POST")
    return ok(await edge("dash-ai", { action: "regenerate_notes", fan_id: m[1] }));
  if (method === "GET" && path === "/scheduled") {
    const { data, error } = await sb.from("dash_scheduled").select("*")
      .eq("user_id", userId).order("time_of_day");
    throwSb(error); return ok(data);
  }
  if (method === "POST" && path === "/scheduled") {
    const { count } = await sb.from("dash_scheduled")
      .select("id", { count: "exact", head: true }).eq("user_id", userId);
    if ((count ?? 0) >= 5) throw { response: { data: { detail: "Limit je 5 správ v rozvrhu" } } };
    const { data, error } = await sb.from("dash_scheduled")
      .insert({ user_id: userId, ...body }).select().single();
    throwSb(error); return ok(data);
  }
  if ((m = P(/^\/scheduled\/([^/]+)$/)) && method === "PATCH") {
    const { data, error } = await sb.from("dash_scheduled").update(body)
      .eq("id", m[1]).eq("user_id", userId).select().single();
    throwSb(error); return ok(data);
  }
  if ((m = P(/^\/scheduled\/([^/]+)$/)) && method === "DELETE") {
    const { error } = await sb.from("dash_scheduled").delete()
      .eq("id", m[1]).eq("user_id", userId);
    throwSb(error); return ok({ ok: true });
  }
  if (method === "POST" && path === "/mass-message/send-drafts")
    return ok(await edge("dash-ai", { action: "mass_send_drafts", ...body }));
  if (method === "POST" && path === "/mass-message")
    return ok(await edge("dash-ai", { action: "mass_message", ...body }));
  if (method === "GET" && path === "/connections/fanvue/start")
    return ok(await edge("dash-fanvue", { action: "oauth_start" }));
  if (method === "POST" && path === "/connections/fanvue/import-history")
    return ok(await edge("dash-fanvue", { action: "import_history", ...body }));
  if (method === "POST" && path === "/ai/regenerate-notes-all")
    return ok(await edge("dash-ai", { action: "regenerate_notes_all", ...body }));
  if (method === "POST" && path === "/connections/fanvue/sync")
    return ok(await edge("dash-fanvue", { action: "sync" }));
  if (method === "POST" && path === "/photos/sync-from-fanvue")
    return ok(await edge("dash-fanvue", { action: "sync_photos" }));
  if (method === "POST" && path === "/photos/upload-to-fanvue")
    return ok(await edge("dash-fanvue", { action: "upload_photo", ...body }));
  if (method === "POST" && path === "/photos/refresh-fanvue-urls")
    return ok(await edge("dash-fanvue", { action: "refresh_photo_urls" }));

  throw apiErr(404, `Neznáma API cesta: ${method} ${path}`);
}

// ── Axios-kompatibilné rozhranie ─────────────────────────────
const api = {
  get: (path, cfg) => route("GET", path, null, cfg?.params),
  post: (path, body, cfg) => route("POST", path, body || {}, cfg?.params),
  put: (path, body, cfg) => route("PUT", path, body || {}, cfg?.params),
  patch: (path, body, cfg) => route("PATCH", path, body || {}, cfg?.params),
  delete: (path, cfg) => route("DELETE", path, null, cfg?.params),
};

export default api;
